/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Grid from "components/generals/Grid";
import Box from "components/generals/Box";
import { FormProps } from "interfaces";
import { HeaderEnum, PatientInterface, PersonInterface, UserInterface, UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";
import { FC, useEffect, useMemo, useState } from "react";
import PatientForm from "./PatientForm";
import PersonForm from "../PersonForm";
import UserForm from "../UserForm";
import FormActionButtons from "../custom/FormActionButtons";
import { H1 } from "components/generals/_Titles";
import { getValidPersonModel, getValidUserModel } from "utils/model.utils";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { useTranslation } from "react-i18next";
import { FormActionType } from "types/form.enum";
import { s } from "@fullcalendar/core/internal-common";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import { useSelector } from "react-redux";

/**
 * Comprehensive form for creating/editing patient information.
 * Combines three sub-forms: PersonForm (demographics), UserForm (credentials), and PatientForm (medical data).
 * Manages state synchronization between the three forms and the main patient model.
 * 
 * Features:
 * - PersonForm: Personal information, address, contacts, emergency contacts, parent/guardian relationships
 * - UserForm: Username, email, password, roles, active status
 * - PatientForm: Account number, SSN, PCP, consents, pharmacy/insurance relationships, documents
 * - Cascading updates: Changes in sub-forms update the main model
 * - Form validation across all three sections
 * - Automatic patient data fetching on mount
 * - List update handling for table refresh
 * - Modal and page modes
 * - Parent relationship management for minors
 * 
 * State Management:
 * - Extracts person and user from patient model
 * - Handles nested updates (patient.person.user)
 * - Fetches complete patient data including relationships
 * - Updates patient list on successful save
 * 
 * Use Cases:
 * - Creating new patient accounts with complete profile
 * - Editing existing patient information
 * - Administrative patient management
 * - Patient registration workflows
 * 
 * @param {FormProps<PatientInterface>} props - Component props
 * @returns {React.ReactElement} The complete patient profile form
 */
const PatientFullForm: FC<FormProps<PatientInterface>> = ({
  model,
  action,
  isModal,
  onCloseAction,
  parentsRelationList,
  modelList,
  setModelList,
}) => {
  const generalSettings = useSelector(selectGeneralSettings);
  const shouldCreatePatientUser = generalSettings?.isPatientUserEnabled ?? true;
  const person = useMemo<Partial<PersonInterface>>(
    () => getValidPersonModel(model.person),
    [model]
  );

  const [patientModel, setPatientModel] = useState<PatientInterface>();
  const user = useMemo<Partial<UserInterface>>(
    () => {
      if (patientModel?.person?.user) {
        return getValidUserModel(patientModel.person.user);
      }
      return model.person ? getValidUserModel(model.person.user) : person.user;
    },
    [model, patientModel, person.user]
  );
  const [_isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  useEffect(() => {
    if (model) {
      sendRequest(CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS, onGetPatient, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: model.id,
        },
      ]);
    }
  }, []);

  const onGetPatient = (patient: PatientInterface) => {
    setPatientModel(patient);
  };

  //TODO: add css into a global class in stylesheet
  /*const headerStyle = useMemo<CSSProperties>(() => {
    let style: CSSProperties = {};
    if (isModal) {
      style = {
        color: "white",
        textShadow: "2px 2px 5px black",
      };
    }
    return style;
  }, [isModal]);*/

  const handlePersonUpdate = (updatedPerson: Partial<PersonInterface>) => {
    const updatedModel: Partial<PatientInterface> = {
      ...model,
      person: {
        ...model.person,
        ...updatedPerson,
      },
    };
    updatePatientList(updatedModel as PatientInterface);
  };

  const handleUserUpdate = (updatedUser: Partial<UserInterface>) => {
    const updatedModel: Partial<PatientInterface> = {
      ...model,
      person: {
        ...model.person,
        user: {
          ...model.person.user,
          ...updatedUser,
          auth: updatedUser.auth || model.person.user?.auth
        },
      },
    };
    updatePatientList(updatedModel as PatientInterface);
  };

  const updatePatientList = (updatedModel: PatientInterface) => {
    const updatedList = modelList?.map((item) =>
      item.id === updatedModel.id ? updatedModel : item
    );
    setModelList(updatedList);
  };

  return (
    <Box className="full-form-wrapper" py={0}>
      <H1 className={isModal ? "title-modal" : ""}>{t("dashboardNavbar.patients")}</H1>
      <Grid container justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Box className="full-form-sub-wrapper">
            {patientModel && (
              <PatientForm
                action={action}
                model={patientModel}
                modelList={modelList}
                setModelList={setModelList}
                comesFromTable={true}
              />
            )}
          </Box>
          <Box className="full-form-sub-wrapper">
            <PersonForm
              action={action}
              type={UserRolesEnum.PATIENT}
              model={person}
              parentsRelationList={parentsRelationList}
              updateListHandler={handlePersonUpdate}
              firstStep={true}
            />
          </Box>
          {shouldCreatePatientUser && (
            <Box className="full-form-sub-wrapper">
              <UserForm action={action} model={user} updateListHandler={handleUserUpdate} person={model.person} type={UserRolesEnum.PATIENT} modifyPassword={true} />
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid className="btn-close-modal">
        {" "}
        <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
      </Grid>
    </Box>
  );
};

export default PatientFullForm;
